import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, BookText, FileCheck2, Radio } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

// Editorial-status flags surfaced on `/active-investigations` (Task C — 2-click activation)
const EDITORIAL_STATUS_OPTIONS = [
    { value: "", label: "— none —" },
    { value: "developing", label: "Developing investigation" },
    { value: "recently_expanded", label: "Recently expanded" },
    { value: "new_evidence", label: "New evidence added" },
    { value: "editorial_focus", label: "Editorial focus" },
    { value: "under_review", label: "Under review" },
    { value: "context_update", label: "Context update" },
];

function StatCard({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="text-xs uppercase tracking-wider text-[#64748B]">{label}</div>
            </div>
            <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
    );
}

export default function AdminDashboard() {
    const [topics, setTopics] = useState([]);
    const [stats, setStats] = useState({ total_topics: 0, published_topics: 0, draft_topics: 0, categories_count: 0 });
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/topics"),
            api.get("/admin/stats"),
        ])
            .then(([t, s]) => {
                setTopics(t.data || []);
                setStats(s.data || {});
            })
            .catch(() => toast.error("Failed to load dashboard"))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this topic? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/topics/${id}`);
            toast.success("Topic deleted");
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const onSetStatus = async (topic, newStatus) => {
        if (topic.status === newStatus) return;
        try {
            await api.put(`/admin/topics/${topic.id}`, { status: newStatus });
            toast.success(`Status → ${newStatus.replace("_", " ")}`);
            load();
        } catch {
            toast.error("Failed to update");
        }
    };

    // Task C — 2-click editorial-status activation (Under investigation / Recently expanded / etc.)
    const onSetEditorialStatus = async (topic, newStatus) => {
        if ((topic.editorial_status || "") === newStatus) return;
        try {
            await api.put(`/admin/topics/${topic.id}/editorial-status`, {
                editorial_status: newStatus || null,
            });
            toast.success(
                newStatus
                    ? `Flagged → ${EDITORIAL_STATUS_OPTIONS.find((o) => o.value === newStatus)?.label || newStatus}`
                    : "Editorial flag cleared"
            );
            load();
        } catch {
            toast.error("Failed to set editorial flag");
        }
    };

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10">
                <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-2">Editorial</div>
                        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
                    </div>
                    <Link
                        to="/admin/topics/new"
                        data-testid="dashboard-new-topic"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2F80ED] hover:bg-[#2F80ED]/90 text-white text-sm font-medium transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        New topic
                    </Link>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard icon={BookText} label="Total topics" value={stats.total_topics || 0} accent="bg-[#2F80ED]/15 text-[#56CCF2]" />
                    <StatCard icon={FileCheck2} label="Published" value={stats.published_topics || 0} accent="bg-[#56CCF2]/15 text-[#56CCF2]" />
                    <StatCard icon={Edit3} label="Drafts" value={stats.draft_topics || 0} accent="bg-[#243044] text-[#94A3B8]" />
                    <StatCard
                        icon={Radio}
                        label="Active flags"
                        value={topics.filter((t) => !!t.editorial_status).length}
                        accent="bg-[#56CCF2]/15 text-[#56CCF2]"
                    />
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#243044] flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">All topics</h2>
                        <span className="text-xs text-[#64748B]">{topics.length} total</span>
                    </div>

                    {loading ? (
                        <div className="p-12 text-center text-sm text-[#64748B]">Loading…</div>
                    ) : topics.length === 0 ? (
                        <div className="p-12 text-center">
                            <p className="text-[#94A3B8] mb-4">No topics yet.</p>
                            <Link to="/admin/topics/new" className="text-sm text-[#56CCF2] hover:text-white">Create your first topic →</Link>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-topics-table">
                                <thead className="bg-[#0B1220] text-[10px] uppercase tracking-wider text-[#64748B]">
                                    <tr>
                                        <th className="px-6 py-3 text-left font-medium">Title</th>
                                        <th className="px-6 py-3 text-left font-medium">Category</th>
                                        <th className="px-6 py-3 text-left font-medium">Status</th>
                                        <th className="px-6 py-3 text-left font-medium">Editorial flag</th>
                                        <th className="px-6 py-3 text-left font-medium">Conf.</th>
                                        <th className="px-6 py-3 text-left font-medium">Concern</th>
                                        <th className="px-6 py-3 text-right font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#243044]">
                                    {topics.map((t) => (
                                        <tr key={t.id} data-testid={`admin-topic-row-${t.slug}`} className="hover:bg-[#0B1220]/60 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="text-white font-medium">{t.title}</div>
                                                <div className="text-xs text-[#64748B] mt-0.5">/{t.slug}</div>
                                            </td>
                                            <td className="px-6 py-4 text-[#94A3B8]">{t.category || "—"}</td>
                                            <td className="px-6 py-4">
                                                <select
                                                    data-testid={`admin-status-${t.slug}`}
                                                    value={t.status || "draft"}
                                                    onChange={(e) => onSetStatus(t, e.target.value)}
                                                    className={`bg-[#0B1220] border rounded-md px-2 py-1 text-[10px] uppercase tracking-wider font-medium focus:outline-none focus:border-[#2F80ED] cursor-pointer ${
                                                        t.status === "published" ? "border-[#56CCF2] text-[#56CCF2]" :
                                                        t.status === "member_only" ? "border-[#F4B731] text-[#F4B731]" :
                                                        t.status === "hidden" ? "border-[#FB7185] text-[#FB7185]" :
                                                        "border-[#243044] text-[#94A3B8]"
                                                    }`}
                                                >
                                                    <option value="draft">Draft</option>
                                                    <option value="member_only">Member only</option>
                                                    <option value="published">Published</option>
                                                    <option value="hidden">Hidden</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {t.editorial_status ? (
                                                        <Radio className="w-3 h-3 text-[#56CCF2] animate-pulse" />
                                                    ) : null}
                                                    <select
                                                        data-testid={`admin-editorial-${t.slug}`}
                                                        value={t.editorial_status || ""}
                                                        onChange={(e) => onSetEditorialStatus(t, e.target.value)}
                                                        className={`bg-[#0B1220] border rounded-md px-2 py-1 text-[10px] uppercase tracking-wider font-medium focus:outline-none focus:border-[#2F80ED] cursor-pointer max-w-[180px] ${
                                                            t.editorial_status
                                                                ? "border-[#56CCF2]/60 text-[#56CCF2]"
                                                                : "border-[#243044] text-[#64748B]"
                                                        }`}
                                                    >
                                                        {EDITORIAL_STATUS_OPTIONS.map((o) => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-[#56CCF2] font-semibold">{t.confidence_score}</td>
                                            <td className="px-6 py-4 text-[#94A3B8] font-semibold">{t.concern_level}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Link
                                                        to={`/admin/topics/${t.id}/edit`}
                                                        data-testid={`admin-edit-${t.slug}`}
                                                        className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#0B1220] transition-colors"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </Link>
                                                    <button
                                                        data-testid={`admin-delete-${t.slug}`}
                                                        onClick={() => onDelete(t.id)}
                                                        className="p-2 rounded-lg text-[#94A3B8] hover:text-red-400 hover:bg-[#0B1220] transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
